import { cloneElement, FC, forwardRef, ReactNode } from 'react'

import { clsx } from 'clsx'

import { CheckIcon } from '../icons/index.js'
import { HTMLTribeProps } from '../types/index.js'
import { FloatingWrapper } from '../utils/floating/index.js'
import { getValidChildren, runIfFn } from '../utils/index.js'
import { useSearchableSelect } from './SearchableSelectContext.js'

export type SearchableSelectItemsProps = HTMLTribeProps<'ul'> & {
  static?: boolean
}
export const SearchableSelectItems = forwardRef<
  HTMLUListElement,
  SearchableSelectItemsProps
>((props, ref) => {
  const { children, className, static: staticProp = false, ...rest } = props

  const { isOpen, getMenuProps } = useSearchableSelect()

  const validChildren = getValidChildren(children)

  const clones = validChildren.map((child, index) => {
    const childProps = {
      index,
      ...child.props,
    }

    return cloneElement(child, childProps)
  })

  const open = isOpen || staticProp

  return (
    <FloatingWrapper open={open} sameWidth skipTransition>
      <ul
        className={clsx(
          'w-full bg-surface shadow-lg max-h-60 rounded-md py-1 text-base border border-line-subdued overflow-auto',
          'focus-visible:ring-1 focus:outline-none',
          !open && 'invisible',
          className,
        )}
        ref={ref}
        {...getMenuProps({}, { suppressRefError: true })}
        {...rest}
      >
        {clones}
      </ul>
    </FloatingWrapper>
  )
})

interface SearchableSelectItemRenderProps {
  selected: boolean
  active: boolean
}
export type SearchableSelectItemProps<T> = Omit<
  HTMLTribeProps<'li'>,
  'value' | 'children'
> & {
  value: T
  index?: number
  disabled?: boolean
  children?: ReactNode | ((props: SearchableSelectItemRenderProps) => ReactNode)
}
export const SearchableSelectItem: FC<
  SearchableSelectItemProps<unknown>
> = props => {
  const { value, children, disabled, index, className, ...rest } = props

  const { selectedItem, getItemProps, highlightedIndex } = useSearchableSelect()

  const selected = selectedItem === value
  const active = highlightedIndex === index

  return (
    <li
      className={clsx(
        active ? 'text-content-on-primary bg-action-primary' : 'text-content',
        'cursor-default select-none relative py-2 ps-3 pe-9',
        className,
      )}
      {...getItemProps({ item: value, index, disabled })}
      {...rest}
    >
      <>
        <span
          className={clsx(
            selected ? 'font-semibold' : 'font-normal',
            disabled ? 'opacity-50' : '',
            'block truncate',
          )}
        >
          {runIfFn(children, {
            selected,
            active,
          })}
        </span>

        {selected ? (
          <span
            className={clsx(
              active ? 'text-content-on-primary' : 'text-action-primary',
              'absolute inset-y-0 end-0 flex items-center pe-4',
            )}
          >
            <CheckIcon className="h-5 w-5" aria-hidden="true" />
          </span>
        ) : null}
      </>
    </li>
  )
}

export type SearchableSelectItemsEmptyProps = HTMLTribeProps<'div'>

export const SearchableSelectItemsEmpty: FC<
  SearchableSelectItemsEmptyProps
> = props => {
  const { children, className, ...rest } = props

  return (
    <div
      className={clsx(
        'text-content',
        'cursor-default select-none relative py-2 ps-3 pe-9',
        className,
      )}
      {...rest}
    >
      <span className="font-normal block truncate">{children}</span>
    </div>
  )
}
